import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Pill } from '@/components/ui/Pill';
import { usersApi, type Lead } from '@/api/users';
import { humanizeError } from '@/lib/errors';
import { openExternal, siteUrl } from '@/lib/externalLink';
import { isStoreBuild } from '@/lib/platform';
import { ActionSheet } from '@/components/mobile/ActionSheet';
import { MChips, MSegmented } from '@/components/mobile/MobileKit';
import { useIsMobile } from '@/hooks/useIsMobile';

/**
 * Заявки на пробное занятие.
 *
 * До 31.08.2026 человек мог написать только в WhatsApp, и заявка жила в
 * переписке менеджера: не видно, сколько их, кто ответил, кто потерялся.
 * Теперь они здесь.
 *
 * ⚠️ Контакты родителя видны отдельной строкой и только у несовершеннолетних:
 * звонить надо именно родителю, а не ребёнку, и менеджер должен это видеть
 * сразу, а не догадываться по возрасту.
 */

const STATUSES = ['new', 'contacted', 'enrolled', 'declined'] as const;
/** Значки статусов в листе «⋯» на телефоне — только из набора шрифта (сторож iconSubset). */
const LEAD_STATUS_ICON: Record<(typeof STATUSES)[number], string> = {
  new: 'schedule',
  contacted: 'hourglass_top',
  enrolled: 'task_alt',
  declined: 'cancel',
};

export function AdminLeadsPage() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const qc = useQueryClient();
  const [status, setStatus] = useState<string>('');
  const [tab, setTab] = useState<'leads' | 'diagnostic'>('leads');
  const [sheetFor, setSheetFor] = useState<Lead | null>(null);

  const list = useQuery({
    queryKey: ['admin', 'leads', status],
    queryFn: () => usersApi.listLeads({ status: status || undefined, limit: 100 }),
  });

  const setLeadStatus = useMutation({
    mutationFn: ({ id, next }: { id: string; next: string }) => usersApi.setLeadStatus(id, next),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'leads'] }),
  });

  const tone = (s: Lead['status']) =>
    s === 'enrolled' ? 'success' : s === 'declined' ? 'neutral' : s === 'contacted' ? 'primary' : 'warning';

  return (
    <>
      <PageHeader
        title={t('adminLeads.title')}
        subtitle={tab === 'diagnostic' ? t('adminLeads.diagSubtitle') : t('adminLeads.subtitle')}
      />

      {/* Заявки и прохождения диагностического теста — два разных списка:
          у теста нет ни возраста, ни родителя, ни состояний «связались». */}
      {/* Телефон (§5.11): вкладки — `MSegmented`, статусы — фишками в одну строку с прокруткой, а не два ряда кнопок. */}
      {isMobile ? (
        <div className="mb-3" data-leads-tabs-phone>
          <MSegmented
            tabs={[
              { key: 'leads', label: t('adminLeads.tabLeads') },
              { key: 'diagnostic', label: t('adminLeads.tabDiagnostic') },
            ]}
            active={tab}
            onChange={setTab}
          />
        </div>
      ) : (
      <div className="mb-4 flex flex-wrap gap-2">
        {(['leads', 'diagnostic'] as const).map((k) => (
          <Button key={k} size="sm" variant={tab === k ? 'primary' : 'secondary'} onClick={() => setTab(k)}>
            {k === 'leads' ? t('adminLeads.tabLeads') : t('adminLeads.tabDiagnostic')}
          </Button>
        ))}
      </div>
      )}

      {tab === 'diagnostic' ? <DiagnosticAttempts /> : <>
      {isMobile ? (
        <div className="mb-3">
          <MChips
            chips={['', ...STATUSES].map((s) => ({ key: s, label: s ? t(`adminLeads.status.${s}`) : t('adminLeads.all') }))}
            active={status}
            onChange={setStatus}
          />
        </div>
      ) : (
      <div className="mb-4 inline-flex flex-wrap gap-2">
        {['', ...STATUSES].map((s) => (
          <Button
            key={s || 'all'}
            size="sm"
            variant={status === s ? 'primary' : 'secondary'}
            onClick={() => setStatus(s)}
          >
            {s ? t(`adminLeads.status.${s}`) : t('adminLeads.all')}
          </Button>
        ))}
      </div>
      )}

      {setLeadStatus.error && (
        <Card variant="subtle" className="mb-4 text-sm text-error">
          {humanizeError(setLeadStatus.error)}
        </Card>
      )}

      {list.isLoading ? (
        <Card variant="subtle">{t('common.loading')}</Card>
      ) : list.error ? (
        <Card variant="subtle" className="text-error">{humanizeError(list.error)}</Card>
      ) : !list.data?.leads?.length ? (
        <Card variant="subtle" className="text-center text-on-surface-variant">
          {t('adminLeads.empty')}
        </Card>
      ) : (
        <div className="grid gap-3">
          {list.data.leads.map((l) => (
            <Card key={l.id} className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-semibold text-on-surface">{l.name}</h3>
                  <Pill variant={tone(l.status)}>{t(`adminLeads.status.${l.status}`)}</Pill>
                  <Pill variant="neutral">{t(`adminLeads.source.${l.source}`)}</Pill>
                  {l.age !== undefined && <Pill variant="neutral">{t('adminLeads.years', { count: l.age })}</Pill>}
                </div>

                {/* На телефоне номер — ссылка для звонка: админ перезванивает прямо со страницы заявок. Без номера
                    ссылки нет — пустая ссылка «звонить» в никуда (кадр стенда 13.10). */}
                {isMobile ? (
                  l.phone && (
                  <a href={`tel:${l.phone}`} className="mt-1 block text-sm font-semibold text-primary" data-lead-call>
                    {l.phone}
                  </a>
                  )
                ) : (
                  <p className="mt-1 text-sm text-on-surface">{l.phone}</p>
                )}
                {l.about && <p className="text-sm text-on-surface-variant">{l.about}</p>}

                {/* Родитель — отдельной строкой и заметно: звонить надо ему. */}
                {l.parent_phone && (
                  <p className="mt-1 text-sm font-medium text-primary">
                    {t('adminLeads.parent')}: {l.parent_name} · {l.parent_phone}
                  </p>
                )}

                <p className="mt-1 text-xs text-on-surface-variant">
                  {new Date(l.created_at).toLocaleString()}
                </p>
              </div>

              {isMobile ? (
                // Телефон (§5.11, правило 4): смена статуса — листом «⋯», а не тремя кнопками в два ряда под заявкой.
                <Button
                  variant="secondary"
                  className="w-full"
                  loading={setLeadStatus.isPending && setLeadStatus.variables?.id === l.id}
                  onClick={() => setSheetFor(l)}
                  data-lead-status-phone
                >
                  <span className="material-symbols-outlined">more_horiz</span>
                  {t(`adminLeads.status.${l.status}`)}
                </Button>
              ) : (
              <div className="flex flex-wrap gap-2">
                {STATUSES.filter((s) => s !== l.status).map((s) => (
                  <Button
                    key={s}
                    size="sm"
                    variant="secondary"
                    loading={setLeadStatus.isPending && setLeadStatus.variables?.id === l.id}
                    onClick={() => setLeadStatus.mutate({ id: l.id, next: s })}
                  >
                    {t(`adminLeads.status.${s}`)}
                  </Button>
                ))}
              </div>
              )}
            </Card>
          ))}
        </div>
      )}
      <ActionSheet
        open={!!sheetFor}
        onClose={() => setSheetFor(null)}
        title={sheetFor?.name}
        actions={
          sheetFor
            ? STATUSES.filter((s) => s !== sheetFor.status).map((s) => ({
                label: t(`adminLeads.status.${s}`),
                icon: LEAD_STATUS_ICON[s],
                onSelect: () => setLeadStatus.mutate({ id: sheetFor.id, next: s }),
              }))
            : []
        }
      />
      </>}
    </>
  );
}

/**
 * Тест на уровень (`/diagnostic`). С 26.10.2026 прохождения и отчёты для
 * сотрудников — на странице самой diagnostic-service (`/diagnostic/admin`),
 * а не здесь: правка теста или вида отчёта выкатывается одной службой, без
 * сайта (решение владельца). Вкладка — только дорога туда.
 *
 * ⚠️ Обычная ссылка, а не `<Link>`: адрес отдаёт шлюз мимо приложения, в
 * маршрутах сайта его нет.
 *
 * ⚠️ В ПРИЛОЖЕНИИ — СИСТЕМНЫМ ОКНОМ БРАУЗЕРА (27.10.2026, сборка 1.1.2): у приложения адрес
 * `capacitor://localhost`, ссылка вела бы в никуда. Вход у окна браузера свой — без него страница
 * сама просит открыть кабинет.
 */
const DIAG_ADMIN = '/diagnostic/admin';

function DiagnosticAttempts() {
  const { t } = useTranslation();
  const app = isStoreBuild();
  return (
    <Card variant="subtle" className="text-center">
      <p className="text-on-surface-variant">{t('adminLeads.diagMoved')}</p>
      <a
        href={DIAG_ADMIN}
        onClick={
          app
            ? (e) => {
                e.preventDefault();
                void openExternal(siteUrl(DIAG_ADMIN));
              }
            : undefined
        }
        className="mt-3 inline-flex rounded-xl bg-primary px-4 py-2 font-semibold text-on-primary"
      >
        {t('adminLeads.diagOpen')}
      </a>
    </Card>
  );
}
